/**
 * CSV Template Generation Utilities
 *
 * Provides functions to generate CSV templates for bulk import of
 * organizational units and locations.
 *
 * @module ImportExportTab/utils/csvTemplates
 */

import Papa from 'papaparse';

/**
 * Get OrgUnit CSV template headers
 *
 * @returns {string[]} Array of header names for OrgUnit CSV template
 */
export function getOrgUnitTemplate() {
  return [
    'name',
    'orgLevel',
    'parentOrgUnitName',
    'isActive'
  ];
}

/**
 * Get Location CSV template headers
 *
 * @returns {string[]} Array of header names for Location CSV template
 */
export function getLocationTemplate() {
  return [
    'locationName',
    'locationNumber',
    'street_1',
    'street_2',
    'city',
    'state',
    'zipCode',
    'timeZone',
    'openDate',
    'ownershipType',
    'conceptId',
    'orgLevel_1_name'
  ];
}

/**
 * Generate sample data for OrgUnit CSV template
 *
 * Provides 2-3 example rows demonstrating proper hierarchy structure
 * with parent-child relationships.
 *
 * @returns {Object[]} Array of sample OrgUnit objects
 */
export function generateOrgUnitSampleData() {
  return [
    {
      name: 'West Region',
      orgLevel: 'ORGLEVEL_3',
      parentOrgUnitName: '',
      isActive: 'TRUE'
    },
    {
      name: 'Bay Area Market',
      orgLevel: 'ORGLEVEL_2',
      parentOrgUnitName: 'West Region',
      isActive: 'TRUE'
    },
    {
      name: 'San Francisco District',
      orgLevel: 'ORGLEVEL_1',
      parentOrgUnitName: 'Bay Area Market',
      isActive: 'TRUE'
    }
  ];
}

/**
 * Generate sample data for Location CSV template
 *
 * Provides 2-3 example rows demonstrating proper location data
 * with required fields and formatting.
 *
 * @returns {Object[]} Array of sample Location objects
 */
export function generateLocationSampleData() {
  return [
    {
      locationName: 'Downtown SF Store',
      locationNumber: '1001',
      street_1: '123 Market Street',
      street_2: 'Suite 100',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94102',
      timeZone: 'America/Los_Angeles',
      openDate: '2024-01-15',
      ownershipType: 'Company',
      conceptId: '',
      orgLevel_1_name: 'San Francisco District'
    },
    {
      locationName: 'Union Square Location',
      locationNumber: '1002',
      street_1: '456 Geary Street',
      street_2: '',
      city: 'San Francisco',
      state: 'CA',
      zipCode: '94108',
      timeZone: 'America/Los_Angeles',
      openDate: '2024-02-01',
      ownershipType: 'Franchise',
      conceptId: '',
      orgLevel_1_name: 'San Francisco District'
    }
  ];
}

/**
 * Convert array of objects to CSV string
 *
 * Uses papaparse to properly format CSV with:
 * - Header row from object keys
 * - Proper quoting for values containing commas
 * - Escaped quotes within values
 * - UTF-8 BOM for Excel compatibility
 *
 * @param {Object[]} data - Array of objects to convert to CSV
 * @returns {string} CSV string with UTF-8 BOM prefix
 */
export function convertToCSVString(data) {
  if (!data || data.length === 0) {
    return '';
  }

  // Use papaparse to convert to CSV with proper escaping
  const csv = Papa.unparse(data, {
    quotes: true,        // Quote fields when necessary
    quoteChar: '"',      // Use double quotes
    escapeChar: '"',     // Escape quotes by doubling them
    header: true,        // Include header row
    newline: '\r\n'      // Windows-style line endings for Excel
  });

  // Add UTF-8 BOM for Excel compatibility
  return '\uFEFF' + csv;
}

/**
 * Generate complete OrgUnit CSV template with headers and sample data
 *
 * @returns {string} Complete CSV string ready for download
 */
export function generateOrgUnitCSV() {
  const sampleData = generateOrgUnitSampleData();
  return convertToCSVString(sampleData);
}

/**
 * Generate complete Location CSV template with headers and sample data
 *
 * @returns {string} Complete CSV string ready for download
 */
export function generateLocationCSV() {
  const sampleData = generateLocationSampleData();
  return convertToCSVString(sampleData);
}
